import { useMemo, useState, ReactNode } from 'react';
import { css } from '@emotion/react';
import {
  AppointmentConfirmationStatus,
  AppointmentLeadTime,
  AppointmentStatus,
  AtRiskStatus,
  TimeFilterType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import {
  DataCard,
  DataCardProps,
  TooltipHeader,
  X_AXIS_TIME_FILTER_OPTIONS,
  formatDailyXAxisTick,
  formatTooltipTitle,
} from '@frontend/analytics-shared';
import { BarClick } from '@frontend/charts';
import { Chart, ChartEditPanel } from '@frontend/charts';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Button, Heading, Text, Truncated } from '@frontend/design-system';
import { APPOINTMENT_TABS } from '../../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../../hooks/use-appointment-analytics-v2-store';
import { useBarClickRedirection } from '../../hooks/use-bar-click-redirection';
import { APPOINTMENT_LEAD_TIME_Y_AXIS_GROUPS } from '../../utils/appointment-lead-time-options';
import { createBarCountTooltipData, getLegendCountsFromSummary } from '../../utils/appointment-tooltip-utils';
import { buildChartSubtitle } from '../../utils/build-chart-subtitle';

type NSRChartYAxis = {
  leadTime: AppointmentLeadTime[];
};

// None of the y-axes are selected by default
const defaultYAxisSelected: NSRChartYAxis = {
  leadTime: [],
};

const colors = {
  confirmed: theme.colors.primary20,
  unconfirmed: theme.colors.warning10,
};
const strokeColors = {
  confirmed: theme.colors.primary50,
  unconfirmed: theme.colorsV2.yellow50(),
};

// Darker shades used for the count text in the tooltip so it stays legible
// against the lighter bar fill colors above.
const labelColors: Record<string, string> = {
  confirmed: theme.colors.primary50,
  unconfirmed: theme.colorsV2.yellow50(),
};

const labels = {
  confirmed: 'Confirmed',
  unconfirmed: 'Unconfirmed',
};

interface Props {
  isOnDashboard?: boolean;
  filters?: AnalyticsGatewayV1.Types.GetAppointmentsNsrDataIO['input'];
}

export const NoShowRateChart = ({ isOnDashboard, filters: propsFilters }: Props) => {
  const { t } = useTranslation('analytics');

  const { filtersInitialized, filters, filterHintText, applyFiltersAndSwitchToTableView } =
    useAppointmentAnalyticsV2ShallowStore(
      'filters',
      'filtersInitialized',
      'filterHintText',
      'applyFiltersAndSwitchToTableView'
    );
  const [selectedXAxis, setSelectedXAxis] = useState<TimeFilterType>(TimeFilterType.DAILY);
  const [selectedYAxes, setSelectedYAxes] = useState<Record<string, string[]>>(defaultYAxisSelected);
  const navigate = useNavigate();
  const currentFilters = filters[APPOINTMENT_TABS.PAST];

  const { data: nsrAggregatesData, isLoading: aggregateDataLoading } =
    AnalyticsGatewayV1.Queries.useGetAppointmentsNsrAggregatesQuery(
      {
        ...filters[APPOINTMENT_TABS.PAST],
        leadTime: selectedYAxes.leadTime?.[0] as AppointmentLeadTime | undefined,
      },
      {
        enabled: filtersInitialized && (filters[APPOINTMENT_TABS.PAST]?.locationId?.length ?? 0) > 0,
      }
    );

  const { data: appointmentsData, isLoading: nsrDataLoading } =
    AnalyticsGatewayV1.Queries.useGetAppointmentsNsrDataQuery(
      propsFilters
        ? { ...propsFilters }
        : {
            ...currentFilters,
            filterType: selectedXAxis,
            leadTime: selectedYAxes.leadTime?.[0] as AppointmentLeadTime | undefined,
          },
      {
        enabled:
          (propsFilters && (propsFilters?.locationId?.length ?? 0) > 0) ||
          (filtersInitialized && (currentFilters?.locationId?.length ?? 0) > 0),
      }
    );
  const handleBarClickRedirect = useBarClickRedirection();

  // Chart Data mapped from API
  const chartData = useMemo(() => {
    if (!appointmentsData?.data) {
      return { groups: [], stackKeys: [] };
    }

    const groups = appointmentsData.data.map((item) => {
      // Keep the raw name alongside the display name so tooltipHeadersByName can
      // look up summaryByRawName without having to reverse the transformation.
      const rawName = item.name ?? '';
      let name = rawName;
      if (selectedXAxis === TimeFilterType.HOURLY) {
        const hour = Number(name);
        if (!isNaN(hour)) {
          const h = hour % 12 || 12;
          name = `${h} ${hour < 12 ? 'am' : 'pm'}`;
        }
      } else if (selectedXAxis === TimeFilterType.DAY_OF_WEEK) {
        name = t(`${name}s`, { ns: 'analytics' });
      } else if (selectedXAxis === TimeFilterType.DAILY) {
        name = formatDailyXAxisTick(name);
      }

      return {
        name,
        rawName,
        values: {
          confirmed: Number(item.confirmed || 0),
          unconfirmed: Number(item.unconfirmed || 0),
        },
      };
    });

    return {
      groups,
      stackKeys: ['confirmed', 'unconfirmed'],
    };
  }, [appointmentsData, selectedXAxis, t]);

  const tooltipTitlesByName = useMemo(() => {
    return (
      chartData?.groups?.reduce<Record<string, ReactNode>>((acc, group) => {
        const rawName = group.rawName;

        acc[group.name] = formatTooltipTitle(rawName, selectedXAxis);
        return acc;
      }, {}) ?? {}
    );
  }, [chartData.groups, selectedXAxis]);

  const tooltipHeadersByName = useMemo(() => {
    return chartData.groups.reduce<Record<string, JSX.Element>>((acc, group) => {
      const total = Object.values(group.values).reduce((sum, val) => sum + val, 0);
      const tooltipTitle = tooltipTitlesByName[group.name] ?? group.name;

      let finalFilterHintText;
      if (selectedXAxis === TimeFilterType.DAILY) {
        finalFilterHintText = dayjs(group.rawName).format('dddd');
      }

      acc[group.name] = (
        <TooltipHeader tooltipTitle={tooltipTitle} filterHintText={finalFilterHintText} total={total} />
      );

      return acc;
    }, {});
  }, [chartData.groups, selectedXAxis, tooltipTitlesByName]);

  // Renders the count value for each stack key in a darker label color instead
  // of the lighter bar fill color used by the default tooltip.
  const customTooltipData = useMemo(
    () =>
      createBarCountTooltipData({
        groups: chartData.groups,
        stackKeys: chartData.stackKeys,
        labelColors,
        labels,
      }),
    [chartData.groups, chartData.stackKeys]
  );

  // Count shown beside each legend label, sourced from the API `summary`.
  const legendValues = useMemo(
    () => getLegendCountsFromSummary(appointmentsData?.summary),
    [appointmentsData?.summary]
  );

  const xAxisTickFormatter = (value: number) => {
    const tickValue = String(value);

    if (selectedXAxis !== TimeFilterType.DAILY) {
      return tickValue;
    }

    return formatDailyXAxisTick(tickValue);
  };

  const cardsData: DataCardProps[] = useMemo(() => {
    const noShowRate = nsrAggregatesData?.noShowRate ?? 0;
    const noShowRateUnconfirmed = nsrAggregatesData?.noShowRateUnconfirmed ?? 0;
    const repeatNoShowAppointmentsCount = nsrAggregatesData?.repeatNoShowAppointmentsCount ?? 0;
    return [
      {
        label: t('No-Show Rate'),
        value: `${noShowRate}%`,
        ctaConfig: {
          icon: 'trend-up',
          text: t('View'),
          onClick: () => {
            applyFiltersAndSwitchToTableView({
              status: [AppointmentStatus.APPOINTMENT_STATUS_NSR],
            });
          },
        },
        isLoading: aggregateDataLoading,
        infoTipData: {
          description: t('% of appointments marked as No-show or not marked as complete'),
        },
      },
      {
        label: t('No-Show Rate for Unconfirmed Appointments'),
        value: `${noShowRateUnconfirmed}%`,
        isLoading: aggregateDataLoading,
        infoTipData: {
          description: t('Unconfirmed appointments that resulted in a no-show'),
        },
      },
      {
        label: t('Repeat No-show Patients'),
        value: `${repeatNoShowAppointmentsCount}`,
        iconData: {
          name: 'user-x',
          color: theme.colors.tangerine50,
          bgColor: theme.colors.tangerine10,
        },
        ctaConfig: {
          icon: 'trend-up',
          text: t('View'),
          onClick: () => {
            applyFiltersAndSwitchToTableView({
              atRiskStatus: [AtRiskStatus.AT_RISK_STATUS_REPEAT_NSR, AtRiskStatus.AT_RISK_STATUS_REPEAT_BOTH],
              status: [AppointmentStatus.APPOINTMENT_STATUS_NSR],
            });
          },
        },
        isLoading: aggregateDataLoading,
        infoTipData: {
          description: t('Patients with more than 2 no-shows in the last 12 months'),
        },
      },
    ];
  }, [nsrAggregatesData, aggregateDataLoading, t, applyFiltersAndSwitchToTableView]);

  const onXChange = (xAxis: string) => {
    setSelectedXAxis(xAxis as TimeFilterType);
  };

  const onYChange = (yAxes: Record<string, string[]>) => {
    setSelectedYAxes(yAxes);
  };

  const subtitle = useMemo(
    () =>
      buildChartSubtitle({
        selectedYAxes,
        yAxisOptions: APPOINTMENT_LEAD_TIME_Y_AXIS_GROUPS.flatMap((g) => g.options),
        suffixMap: { leadTime: 'lead time' },
        filterHintText,
        t,
      }),
    [t, selectedYAxes, filterHintText]
  );

  const handleOnBarClick = (args: BarClick) => {
    const statusMapping: Record<string, AppointmentConfirmationStatus> = {
      confirmed: AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_CONFIRMED,
      unconfirmed: AppointmentConfirmationStatus.APPOINTMENT_CNF_STATUS_UNCONFIRMED,
    };
    const confirmationStatus = statusMapping[args.barId];

    handleBarClickRedirect(
      args,
      chartData.groups,
      confirmationStatus
        ? {
            confirmationStatus,
            status: [AppointmentStatus.APPOINTMENT_STATUS_NSR],
            leadTime: selectedYAxes.leadTime?.[0] as AppointmentLeadTime | undefined,
          }
        : {}
    );
  };

  const chartTitle = t('No-Show Rate by Confirmation Status');

  const chart = (
    <Chart
      colors={colors}
      labels={labels}
      isLoading={nsrDataLoading}
      strokeColors={strokeColors}
      css={{
        '.chart-content': {
          padding: theme.spacing(isOnDashboard ? 3 : 2),
        },
      }}
    >
      <Chart.HeaderV2
        title={
          <Chart.HeaderV2.Title>
            {isOnDashboard ? chartTitle : <Text size={isOnDashboard ? 'inherit' : 'large'}>{chartTitle}</Text>}
          </Chart.HeaderV2.Title>
        }
        subTitle={
          !isOnDashboard && subtitle ? (
            <Truncated color='subdued' as={Text} size='medium' css={{ maxWidth: '700px' }}>
              {subtitle}
            </Truncated>
          ) : undefined
        }
        action={
          isOnDashboard ? (
            <Button
              size='small'
              variant='tertiary'
              onClick={() => {
                navigate({
                  to: '/analytics/appointment',
                  search: (prev: any) => ({ ...prev }),
                });
              }}
              trackingId='show-more-nsr-chart-appointment-analytics'
            >
              {t('Show More')}
            </Button>
          ) : (
            <ChartEditPanel
              xAxisOptions={X_AXIS_TIME_FILTER_OPTIONS}
              yAxisGroups={APPOINTMENT_LEAD_TIME_Y_AXIS_GROUPS}
              defaultYAxisSelected={defaultYAxisSelected}
              defaultXAxisSelected={TimeFilterType.DAILY}
              onXChange={onXChange}
              onYChange={onYChange}
            />
          )
        }
      />
      <Chart.BarChart
        data={chartData}
        appearance={{
          mode: 'stacked',
          barSize: 24,
          showXAxis: true,
          showYAxis: true,
          height: isOnDashboard ? 280 : 240,
          barRadius: 4,
          hideMarkers: true,
          customTooltipTitle: ({ groupName }) =>
            tooltipHeadersByName[groupName] ?? tooltipTitlesByName[groupName] ?? groupName,
          customTooltipData,
          customXAxisTickFormat: xAxisTickFormatter,
        }}
        onClick={!isOnDashboard && selectedXAxis === TimeFilterType.DAILY ? handleOnBarClick : undefined}
      />
      <Chart.Legends doNotCollapseLegends values={legendValues} />
    </Chart>
  );

  if (isOnDashboard) {
    return chart;
  }

  return (
    <section css={styles.outerContainer}>
      <Heading
        level={3}
        css={{
          lineHeight: theme.spacing(3),
        }}
      >
        {t('No-Show Rate')}
      </Heading>

      <div css={styles.cardsGrid}>
        {cardsData.map((card) => (
          <DataCard key={card.label} {...card} />
        ))}
      </div>

      {chart}
    </section>
  );
};

const styles = {
  outerContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2)};
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.spacing(1.5)};
  `,
  mainTitle: css`
    font-size: ${theme.fontSize(20)};
    font-weight: ${theme.font.weight.bold};
    color: ${theme.colors.neutral90};
  `,
  cardsGrid: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
  `,
  chartPlaceholderWrapper: css``,
};
